import { Header, Spinner } from 'view/components';
import { useEffect, useState } from 'react';
import { NoResultsBlock } from 'view/pages/candidates/components';
import * as CS from 'view/pages/candidates/commonStyled';
import { useNavigate } from 'react-router';
import { AppliedStatusPurpleIcon, InvitedStatusPurpleIcon } from 'assets';
import {
  APPLICATIONS_PER_PAGE,
  useGetMyApplications,
} from 'view/pages/profile/hooks/useGetMyApplications';
import { JobPostsListItem } from 'view/pages/profile/components';
import * as S from './styled';

const ApplicationsPage = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [appliedPage, setAppliedPage] = useState(1);
  const [invitedPage, setInvitedPage] = useState(1);

  const params = new URLSearchParams(window.location.search);
  const applicantType = params.get('applicantType');

  const {
    isLoading: isAllApplicationsLoading,
    getAllApplications,
    data: allApplicationsData,
  } = useGetMyApplications();

  const {
    isLoading: isAppliedApplicationsLoading,
    getAppliedApplications,
    data: appliedApplicationsData,
  } = useGetMyApplications();

  const {
    isLoading: isInvitedApplicationsLoading,
    getInvitedApplications,
    data: invitedApplicationsData,
  } = useGetMyApplications();

  useEffect(() => {
    getAllApplications();
    getAppliedApplications();
    getInvitedApplications();
  }, []);

  const updateUrl = (applicantType: string) => {
    const params = new URLSearchParams(window.location.search);
    params.set('applicantType', applicantType);
    navigate({
      pathname: window.location.pathname,
      search: `?${params.toString()}`,
    });
  };

  return (
    <S.Wrapper>
      <Header />
      <S.TabsContainer>
        <S.TabItem
          selected={applicantType === 'all'}
          onClick={() => {
            updateUrl('all');
          }}
          style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
        >
          <p>All ({allApplicationsData?.total || 0})</p>
        </S.TabItem>
        <S.TabItem
          selected={applicantType === 'applied'}
          onClick={() => {
            updateUrl('applied');
          }}
          style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
        >
          <AppliedStatusPurpleIcon selected={applicantType === 'applied'} />
          <p>Applied ({appliedApplicationsData?.total || 0})</p>
        </S.TabItem>
        <S.TabItem
          selected={applicantType === 'invited'}
          onClick={() => {
            updateUrl('invited');
          }}
          style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
        >
          <InvitedStatusPurpleIcon selected={applicantType === 'invited'} />
          <p>Invitations ({invitedApplicationsData?.total || 0})</p>
        </S.TabItem>
      </S.TabsContainer>
      {applicantType === 'all' && (
        <S.MainContentWrapper>
          {isAllApplicationsLoading && (
            <Spinner
              variant="outlined"
              style={{
                position: 'absolute',
                top: '0',
                left: '0',
                backgroundColor: 'rgba(217, 225, 255, 0.3)',
                width: '100%',
                height: '100%',
                zIndex: 10,
              }}
            />
          )}
          {!isAllApplicationsLoading && !allApplicationsData?.total && (
            <NoResultsBlock title="🕵️‍♂️ No Applications Found Here" subTitle="" />
          )}
          {allApplicationsData?.total && !!allApplicationsData?.results?.length ? (
            <>
              <S.CandidatesListBlockMainWrapper>
                <S.ApplicantsListBlockTitle>
                  {allApplicationsData?.total} applications
                </S.ApplicantsListBlockTitle>
                {allApplicationsData?.results?.map((item) => {
                  return <JobPostsListItem candidate={item} key={item.id} queryAfterSubmit="" />;
                })}
              </S.CandidatesListBlockMainWrapper>
              <CS.CustomPagination
                count={Math.ceil(allApplicationsData.total / APPLICATIONS_PER_PAGE)}
                shape="rounded"
                page={page}
                onChange={(e, page) => {
                  setPage(page);
                  getAllApplications(page);
                }}
                style={{ margin: 'auto 0 0 auto' }}
              />
            </>
          ) : null}
        </S.MainContentWrapper>
      )}
      {applicantType === 'applied' && (
        <S.MainContentWrapper>
          {isAppliedApplicationsLoading && (
            <Spinner
              variant="outlined"
              style={{
                position: 'absolute',
                top: '0',
                left: '0',
                backgroundColor: 'rgba(217, 225, 255, 0.3)',
                width: '100%',
                height: '100%',
                zIndex: 10,
              }}
            />
          )}
          {!isAppliedApplicationsLoading && !appliedApplicationsData?.total && (
            <NoResultsBlock title="🕵️‍♂️ No Applications Found Here" subTitle="" />
          )}
          {appliedApplicationsData?.total && !!appliedApplicationsData?.results?.length ? (
            <>
              <S.CandidatesListBlockMainWrapper>
                <S.ApplicantsListBlockTitle>
                  {appliedApplicationsData?.total} applications
                </S.ApplicantsListBlockTitle>
                {appliedApplicationsData?.results?.map((item) => {
                  return <JobPostsListItem candidate={item} key={item.id} queryAfterSubmit="" />;
                })}
              </S.CandidatesListBlockMainWrapper>
              <CS.CustomPagination
                count={Math.ceil(appliedApplicationsData.total / APPLICATIONS_PER_PAGE)}
                shape="rounded"
                page={appliedPage}
                onChange={(e, page) => {
                  setAppliedPage(page);
                  getAppliedApplications(page);
                }}
                style={{ margin: 'auto 0 0 auto' }}
              />
            </>
          ) : null}
        </S.MainContentWrapper>
      )}
      {applicantType === 'invited' && (
        <S.MainContentWrapper>
          {isInvitedApplicationsLoading && (
            <Spinner
              variant="outlined"
              style={{
                position: 'absolute',
                top: '0',
                left: '0',
                backgroundColor: 'rgba(217, 225, 255, 0.3)',
                width: '100%',
                height: '100%',
                zIndex: 10,
              }}
            />
          )}
          {!isInvitedApplicationsLoading && !invitedApplicationsData?.total && (
            <NoResultsBlock
              title="🕵️‍♂️ Waiting on Invitations"
              subTitle="When recruiters invite you to apply, they’ll show up here."
            />
          )}
          {invitedApplicationsData?.total && !!invitedApplicationsData?.results?.length ? (
            <>
              <S.CandidatesListBlockMainWrapper>
                <S.ApplicantsListBlockTitle>
                  {invitedApplicationsData?.total} applications
                </S.ApplicantsListBlockTitle>
                {invitedApplicationsData?.results?.map((item) => {
                  return <JobPostsListItem candidate={item} key={item.id} queryAfterSubmit="" />;
                })}
              </S.CandidatesListBlockMainWrapper>
              <CS.CustomPagination
                count={Math.ceil(invitedApplicationsData.total / APPLICATIONS_PER_PAGE)}
                shape="rounded"
                page={invitedPage}
                onChange={(e, page) => {
                  setInvitedPage(page);
                  getInvitedApplications(page);
                }}
                style={{ margin: 'auto 0 0 auto' }}
              />
            </>
          ) : null}
        </S.MainContentWrapper>
      )}
    </S.Wrapper>
  );
};

export default ApplicationsPage;
